import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Field, Label, Input, FluentProvider, webLightTheme } from '@fluentui/react-components';
import React, { ChangeEvent } from 'react';
import { Controller, UseFormMethods } from 'react-hook-form';
import { RequiredMarker } from '../../common/formFields/RequiredMarker';
import RequiredFieldsNotification from '../RequiredNotification';
import MsReportingSalesLocationDropDown from './MsReportingSalesLocationDropDown';
import MsReportingSalesYearSelector from './MsReportingSalesYearSelector';
import { ISapSetupInfo, RequiredFieldsType } from './types';
import IDropdownOption from '../../../utils/types/IDropdownOption';

interface ISapSetupFormProps {
  form: UseFormMethods<ISapSetupInfo>;
  data: ISapSetupInfo;
}

const SapSetupForm = ({ data, form }: ISapSetupFormProps): JSX.Element => {
  const { control, errors, setValue } = form;

  const onMsReportingSalesLocationChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const selectedOption = event.target.value;
    const selectedText = event.target.options[event.target.selectedIndex]?.text;
    const value = { id: selectedOption, name: selectedText };
    setValue('msReportingSalesLocation', value);
  };

  const onMsReportingSalesYearChange = (
    event: ChangeEvent<HTMLSelectElement>,
    option?: IDropdownOption,
    index?: number,
  ): void => {
    const selectedYear =
      option?.key !== undefined && !Number.isNaN(Number(option.key))
        ? Number(option.key)
        : (null as unknown as number);

    setValue('msReportingSalesYear', selectedYear);
  };

  const onTaxJurisdictionCodeChange = (newValue?: string) => {
    // Remove all symbols except dash (-) from input
    const cleanedInput = newValue.replace(/[^a-zA-Z0-9-]/g, '');

    setValue('taxJurisdictionCode', cleanedInput);
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 5 }}>
        <RequiredFieldsNotification type={RequiredFieldsType.SapSetup} />
        <Label style={{ paddingBottom: 0 }}>
          Tax Jurisdiction Code <RequiredMarker />
        </Label>
        <Controller
          name="taxJurisdictionCode"
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <Field validationMessage={errors?.taxJurisdictionCode?.message}>
              <Input
                id="taxJurisdictionCode"
                name={name}
                value={value || ''}
                onChange={(e, inputData) => onTaxJurisdictionCodeChange(inputData.value)}
              />
            </Field>
          )}
          control={control}
          defaultValue={data?.taxJurisdictionCode || ''}
          rules={{
            maxLength: {
              value: 100,
              message: 'Tax Jurisdiction Code can only be a max of 100 characters',
            },
          }}
        />
        <Controller
          name="msReportingSalesYear"
          control={control}
          defaultValue={data?.msReportingSalesYear}
          render={(): JSX.Element => {
            return (
              <MsReportingSalesYearSelector
                defaultValue={data?.msReportingSalesYear}
                onChange={onMsReportingSalesYearChange}
              />
            );
          }}
        />
        <Controller
          name="msReportingSalesLocation"
          control={control}
          defaultValue={data?.msReportingSalesLocation}
          render={(): JSX.Element => {
            return (
              <MsReportingSalesLocationDropDown
                defaultValue={data?.msReportingSalesLocation?.id}
                onChange={onMsReportingSalesLocationChange}
              />
            );
          }}
        />
      </Stack>
    </FluentProvider>
  );
};

export default SapSetupForm;
